import { MCP_PERMISSION, type WebPermission } from '@dawn/feature-config';
import { SettingsNav } from '@dawn/ui';
import { Plug, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { NoAccess } from '@/components/NoAccess';
import { AdminsSection } from '@/components/settings/AdminsSection';
import { McpConnectedAppsSection } from '@/components/settings/McpConnectedAppsSection';
import { usePermissions } from '@/hooks/use-permissions';

interface SettingsSection {
  id: string;
  label: string;
  description: string;
  icon: ReactNode;
  /**
   * Any of these opens the section. Without all of them the nav entry stays and
   * the pane shows NoAccess, giving the first as the reason.
   */
  permissions: readonly [WebPermission, ...WebPermission[]];
  Section: () => JSX.Element;
}

/**
 * Settings page. Sections are switched via the standout side nav (`SettingsNav`);
 * the active section lives in the URL path (`/settings/:section`) so it survives
 * refresh and is deep-linkable/bookmarkable.
 *
 * Each section declares the permissions any of which opens it (TDEV-1213). Every section stays
 * in the nav; one the admin lacks shows `NoAccess` in the pane and is never
 * mounted, so its data is never requested (the server checks every request
 * anyway). Bare `/settings` and unknown sections land on the first section the
 * admin can use, never outside Settings.
 *
 * Add a new section const below and append it to `SETTINGS_SECTIONS` as more
 * settings land.
 */
const MCP_SECTION: SettingsSection = {
  id: 'mcp',
  label: 'MCP',
  description: 'Connected apps & client setup',
  icon: <Plug className="h-[18px] w-[18px]" />,
  // Data-role holders connect and manage their own clients, Super Admins every
  // Admin's (TDEV-1234); the section shows each what they can act on.
  permissions: [MCP_PERMISSION, 'users.manage'],
  Section: McpConnectedAppsSection,
};

const ADMINS_SECTION: SettingsSection = {
  id: 'admins',
  label: 'User Management',
  description: 'Team access & roles',
  icon: <Users className="h-[18px] w-[18px]" />,
  permissions: ['users.view'],
  Section: AdminsSection,
};

const SETTINGS_SECTIONS: SettingsSection[] = [MCP_SECTION, ADMINS_SECTION];

export function SettingsPage() {
  // No in-page title here — the app header already labels the page "Settings".
  // The section switcher carries the sub-navigation; each section owns its own
  // heading via its Card.
  const navigate = useNavigate();
  const { section } = useParams();
  const { can } = usePermissions();
  const canUse = (s: SettingsSection) => s.permissions.some(can);
  const activeSection = SETTINGS_SECTIONS.find((s) => s.id === section);

  // MCP when the admin can use no section.
  if (!activeSection) {
    const landing = SETTINGS_SECTIONS.find(canUse) ?? MCP_SECTION;
    return <Navigate to={`/settings/${landing.id}`} replace />;
  }

  const handleSelect = (id: string) => {
    // Replace (not push) so flipping between sections doesn't stack history —
    // back from a settings tab leaves Settings rather than cycling tabs.
    navigate(`/settings/${id}`, { replace: true });
  };

  const { Section: ActiveSection } = activeSection;

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <SettingsNav items={SETTINGS_SECTIONS} activeId={activeSection.id} onSelect={handleSelect} />
      <div className="min-w-0 flex-1">
        {canUse(activeSection) ? (
          <ActiveSection />
        ) : (
          <NoAccess permission={activeSection.permissions[0]} />
        )}
      </div>
    </div>
  );
}
